import { useMemo } from "react";

import { buttonVariants } from "@/design-system/Button";
import { twResolve } from "@/helpers/tw-resolve";

export interface PaginationProps {
  count: number;
  currentIndex: number;
  onChange: (newIndex: number) => void;
  previousLabel: string;
  nextLabel: string;
  boundaryCount?: number;
  siblingsCount?: number;
}
type View = "simple" | "truncatedMiddle" | "truncatedStart" | "truncatedEnd" | "truncatedBoth";
export function Pagination(props: PaginationProps): React.ReactNode {
  const { onChange, currentIndex, count, previousLabel, nextLabel, boundaryCount = 2, siblingsCount = 1 } = props;
  const pageIndexes = useMemo(() => {
    return Array.from(
      {
        length: count,
      },
      (_, i) => i,
    );
  }, [count]);
  const view = getView(currentIndex, count, boundaryCount, siblingsCount);

  return (
    <nav className="flex items-center justify-between gap-space-sm">
      <PaginationControl
        direction="previous"
        label={previousLabel}
        disabled={currentIndex === 0}
        onClick={() => onChange(currentIndex - 1)}
      />
      <div className="hidden px-space-md @md:flex @md:gap-space-lg">
        {view === "simple"
          ? pageIndexes.map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))
          : null}
        {view === "truncatedMiddle" ? (
          <>
            {pageIndexes.slice(0, boundaryCount).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
            <span className="inline-flex items-center text-text-secondary">...</span>
            {pageIndexes.slice(boundaryCount * -1).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
          </>
        ) : null}
        {view === "truncatedEnd" ? (
          <>
            {pageIndexes.slice(0, currentIndex + siblingsCount + 1).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
            <span className="inline-flex items-center text-text-secondary">...</span>
            {pageIndexes.slice(-boundaryCount).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
          </>
        ) : null}
        {view === "truncatedBoth" ? (
          <>
            {pageIndexes.slice(0, boundaryCount - 1).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
            <span className="inline-flex items-center text-text-secondary">...</span>
            {pageIndexes.slice(currentIndex - siblingsCount, currentIndex + siblingsCount + 1).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
            <span className="inline-flex items-center text-text-secondary">...</span>
            {pageIndexes.slice(-(boundaryCount - 1)).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
          </>
        ) : null}
        {view === "truncatedStart" ? (
          <>
            {pageIndexes.slice(0, boundaryCount).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
            <span className="inline-flex items-center text-text-secondary">...</span>
            {pageIndexes.slice(currentIndex - siblingsCount).map((pageIndex) => (
              <PageButton
                key={pageIndex}
                pageIndex={pageIndex}
                isCurrent={pageIndex === currentIndex}
                onChange={onChange}
              />
            ))}
          </>
        ) : null}
      </div>
      <PaginationControl
        direction="next"
        label={nextLabel}
        disabled={currentIndex === props.count - 1}
        onClick={() => onChange(currentIndex + 1)}
      />
    </nav>
  );
}

interface PageButtonProps {
  pageIndex: number;
  isCurrent: boolean;
  onChange: (newIndex: number) => void;
}

// The current page stays a real button marked with aria-current instead of a
// disabled one, so it reads as emphasized rather than greyed out.
function PageButton({ pageIndex, isCurrent, onChange }: PageButtonProps): React.ReactNode {
  return (
    <button
      type="button"
      aria-current={isCurrent ? "page" : undefined}
      onClick={isCurrent ? undefined : () => onChange(pageIndex)}
      className={twResolve(
        buttonVariants({ variant: "ghostSecondary", content: "body" }),
        isCurrent ? "cursor-default text-body-emphasis" : "text-text-secondary hocus:text-text-primary",
      )}
    >
      {pageIndex + 1}
    </button>
  );
}

interface PaginationControlProps {
  direction: "previous" | "next";
  label: string;
  disabled: boolean;
  onClick: () => void;
}

function PaginationControl({ direction, label, disabled, onClick }: PaginationControlProps): React.ReactNode {
  const path = direction === "previous" ? "M15 18L9 12L15 6" : "M9 18L15 12L9 6";

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={twResolve(
        buttonVariants({ variant: "soft", size: "sm", shape: "circular" }),
        "flex size-icon-display! items-center justify-center px-0!",
      )}
    >
      <svg aria-hidden="true" className="size-icon-inline" viewBox="0 0 24 24" fill="none">
        <path d={path} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function getView(currentIndex: number, count: number, boundaryCount: number, siblingsCount: number): View {
  const baseNumber = 4;
  const boundaryStartEdgeIndex = boundaryCount - 1;
  const boundaryEndEdgeIndex = count - boundaryCount;
  let type: View = "simple";
  const isSimple = count <= baseNumber + boundaryCount;
  const isTruncatedEnd =
    !isSimple && currentIndex >= boundaryStartEdgeIndex && currentIndex <= boundaryStartEdgeIndex + siblingsCount * 2;
  const isTruncatedStart =
    !isSimple && currentIndex <= boundaryEndEdgeIndex && currentIndex >= boundaryEndEdgeIndex - siblingsCount * 2;
  const isTruncatedBoth = !isSimple && currentIndex > boundaryStartEdgeIndex && !isTruncatedEnd && !isTruncatedStart;
  const isTruncatedMiddle = !isSimple && (currentIndex < boundaryStartEdgeIndex || currentIndex > boundaryEndEdgeIndex);
  if (isSimple) {
    return type;
  }
  if (isTruncatedEnd) {
    type = "truncatedEnd";
  }
  if (isTruncatedStart) {
    type = "truncatedStart";
  }
  if (isTruncatedBoth) {
    type = "truncatedBoth";
  }
  if (isTruncatedMiddle) {
    type = "truncatedMiddle";
  }

  return type;
}
